
import { useState, useEffect } from "react";
import { DocumentCard } from "@/components/DocumentCard";
import { DocumentForm } from "@/components/document/DocumentForm";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, BookOpen, Clipboard, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type DocumentType = "oficio" | "requerimento" | "discurso" | "memorando" | null;

const Index = () => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentType>(null);
  const [backgroundUrl, setBackgroundUrl] = useState<string>("/placeholder.svg");

  useEffect(() => {
    const fetchBackgroundUrl = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('background_url')
          .limit(1)
          .single()

        if (error) throw error
        if (data?.background_url) {
          setBackgroundUrl(data.background_url)
        }
      } catch (error) {
        console.error('Error fetching background:', error)
      }
    }

    fetchBackgroundUrl()
  }, [])

  const handleDocumentGenerated = (text: string) => {
    console.log("Document generated:", selectedDocument, text);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const documentTypes = [
    {
      type: "oficio" as const,
      title: "Ofício",
      description: "Documento oficial para comunicação entre autoridades ou instituições. Ideal para solicitações, informações e comunicados formais.",
      icon: <FileText className="w-12 h-12 text-primary" />
    },
    {
      type: "requerimento" as const,
      title: "Requerimento",
      description: "Documento formal para fazer pedidos ou solicitações específicas a uma autoridade ou órgão público.",
      icon: <Clipboard className="w-12 h-12 text-primary" />
    },
    {
      type: "discurso" as const,
      title: "Discurso",
      description: "Prepare manifestações verbais estruturadas para sessões, apresentando projetos, políticas ou demandas de forma eloquente.",
      icon: <BookOpen className="w-12 h-12 text-primary" />
    },
    {
      type: "memorando" as const,
      title: "Memorando",
      description: "Comunicação interna oficial entre departamentos ou setores, para transmitir informações, solicitar providências ou fazer encaminhamentos.",
      icon: <ScrollText className="w-12 h-12 text-primary" />
    }
  ];

  if (selectedDocument) {
    return (
      <div className="min-h-screen w-full">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            className="mb-4 md:mb-8 text-primary hover:text-primary/80 flex items-center gap-2 group"
            onClick={() => setSelectedDocument(null)}
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            Voltar para seleção de documentos
          </Button>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-4 md:p-8 shadow-lg"
          >
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {selectedDocument === "oficio" && "Criar Novo Ofício"}
              {selectedDocument === "requerimento" && "Criar Novo Requerimento"}
              {selectedDocument === "discurso" && "Criar Novo Discurso"}
              {selectedDocument === "memorando" && "Criar Novo Memorando"}
            </h1>
            
            <DocumentForm
              type={selectedDocument}
              onDocumentGenerated={handleDocumentGenerated}
            />
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col">
      <div className="relative h-[30vh] md:h-[40vh] w-full overflow-hidden group rounded-[15px]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 z-10 transition-all duration-500 group-hover:from-black/50 group-hover:via-black/30 group-hover:to-black/50 rounded-[15px]"></div>
        <img 
          src={backgroundUrl}
          alt="Banner"
          className="w-full h-full object-cover object-center transform transition-transform duration-700 scale-100 group-hover:scale-105 rounded-[15px]"
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center text-center px-4">
          <div className="max-w-4xl space-y-6">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 tracking-tight drop-shadow-lg"
            >
              Assistente Legislativo
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-base md:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md backdrop-blur-sm bg-black/10 p-4 rounded-2xl"
            >
              Selecione o tipo de documento que deseja criar e deixe nosso assistente ajudar você a produzir um conteúdo profissional e adequado.
            </motion.p>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full bg-gradient-to-b from-white to-primary/5 py-8 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full filter blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          <motion.div 
            className="max-w-5xl mx-auto"
            initial="hidden"
            animate="show"
            variants={container}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documentTypes.map((doc) => (
                <motion.div key={doc.type} variants={item} className="w-full">
                  <DocumentCard
                    title={doc.title}
                    description={doc.description}
                    icon={doc.type}
                    onClick={() => setSelectedDocument(doc.type)}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Index;
